import { useId, useMemo, type ReactNode } from 'react';
import * as Popover from '@radix-ui/react-popover';
import styles from './Tour.module.css';
import type { TourStep } from './types';

interface TourTooltipProps {
  step: TourStep;
  currentIndex: number;
  total: number;
  /** True when this is the final step (renders Done instead of Next). */
  isLast: boolean;
  onNext: () => void;
  onSkip: () => void;
}

/**
 * Splits a body string on `**…**` segments and renders the inside as <strong>.
 * Cheap stand-in for full markdown — body strings are short and authored.
 */
function renderBoldMarkers(body: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = regex.exec(body)) !== null) {
    if (match.index > lastIndex) parts.push(body.slice(lastIndex, match.index));
    parts.push(<strong key={i++}>{match[1]}</strong>);
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < body.length) parts.push(body.slice(lastIndex));
  return parts;
}

export function TourTooltip({ step, currentIndex, total, isLast, onNext, onSkip }: TourTooltipProps) {
  const titleId = useId();
  const bodyId = useId();
  const Icon = step.icon;
  const bodyParts = useMemo(() => renderBoldMarkers(step.body), [step.body]);

  return (
    <Popover.Content
      side={step.side}
      align={step.align}
      sideOffset={12}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      className={`${styles.popperContent} echo-tour-popper-content`}
      onOpenAutoFocus={(e) => {
        // Let the dialog mount, then move focus to the primary CTA so keyboard
        // users land on Next/Done rather than the × button.
        e.preventDefault();
        const root = e.currentTarget as HTMLElement | null;
        root?.querySelector<HTMLElement>(`.${styles.primary}`)?.focus();
      }}
      onEscapeKeyDown={(e) => {
        e.preventDefault();
        onSkip();
      }}
      onPointerDownOutside={(e) => {
        // Backdrop click is non-dismissive per spec.
        e.preventDefault();
      }}
      onInteractOutside={(e) => {
        e.preventDefault();
      }}
    >
      {/* The visual card. Keyed by step.id so React remounts it on every
          step change — that re-triggers the scale-from-chevron keyframe
          in Tour.module.css. Popover.Content above stays mounted so Radix
          doesn't see a close/reopen cycle on Next clicks. data-side +
          data-align mirror Radix's auto-set attributes so existing CSS
          selectors (chevron position, transform-origin) keep matching. */}
      <div
        key={step.id}
        className={styles.card}
        data-side={step.side}
        data-align={step.align}
      >
        <button type="button" className={styles.close} onClick={onSkip} aria-label="Close tour">
          ×
        </button>
        <div className={styles.stepBody}>
          <div className={styles.iconWrap}>
            <Icon aria-hidden="true" />
          </div>
          <h2 id={titleId} className={styles.title}>{step.title}</h2>
          <p id={bodyId} className={styles.body}>{bodyParts}</p>
        </div>
        <div className={styles.footer}>
          <span className={styles.counter}>{currentIndex + 1} of {total}</span>
          <div className={styles.actions}>
            <button type="button" className={styles.skip} onClick={onSkip} data-testid="tour-skip-btn">
              Skip
            </button>
            <button
              type="button"
              className={styles.primary}
              onClick={onNext}
              data-testid={isLast ? 'tour-done-btn' : 'tour-next-btn'}
            >
              {isLast ? 'Done' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </Popover.Content>
  );
}
